import React, { useState, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Collapse from "@material-ui/core/Collapse";
import CircularProgress from "@material-ui/core/CircularProgress";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ClearIcon from "@material-ui/icons/Clear";

import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";
import { StatusBadge } from "../../components/ui";
import { Card, SectionHead, MethodBadge, FieldLabel, useUi } from "./ui";
import CodeBlock from "./CodeBlock";
import { buildRequest, generateSnippets, LANGS, MODES } from "./snippets";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    grid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: 20,
      [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "minmax(0, 5fr) minmax(0, 7fr)",
        gap: 24,
      },
    },
    form: { display: "flex", flexDirection: "column", gap: 14, minWidth: 0 },
    // tipo de envio: abas do tema (sublinhado na cor da marca) sobre um filete
    modeTabs: {
      minHeight: 40,
      borderBottom: `1px solid ${t.line}`,
      "& .MuiTab-root": { minHeight: 40, minWidth: 0, padding: "6px 12px", fontSize: 13 },
    },
    row2: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: 14,
      [theme.breakpoints.up("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    },
    fileRow: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    fileName: {
      fontFamily: t.font,
      fontSize: 13,
      color: t.textSecondary,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    advToggle: {
      alignSelf: "flex-start",
      marginLeft: -8,
      "& svg": { transition: "transform 150ms ease" },
    },
    advOpen: { "& svg": { transform: "rotate(180deg)" } },
    switches: { display: "flex", flexWrap: "wrap", gap: "0 16px" },
    switchLabel: { fontFamily: t.font, fontSize: 13, color: t.text },
    right: { display: "flex", flexDirection: "column", gap: 12, minWidth: 0 },
    endpoint: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      minWidth: 0,
    },
    testRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    testHint: { fontFamily: t.font, fontSize: 12, color: t.textMuted },
    resultHead: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontFamily: t.font,
      fontSize: 12,
      color: t.textSecondary,
    },
  };
});

const MODE_LABELS = {
  // TODO i18n
  text: "Texto",
  media: "Arquivo",
  image: "Imagem por URL",
  check: "Verificar número",
};

const initial = {
  number: "",
  body: "",
  url: "",
  caption: "",
  userId: "",
  queueId: "",
  sendSignature: false,
  closeTicket: false,
};

/**
 * Bloco 2 — construtor interativo: escolhe conexão + tipo + campos → gera
 * curl/Node/Python/PHP com o token e a URL reais e dispara o teste de verdade.
 */
const Builder = ({ whatsApps = [], users = [], queues = [], selectedWhatsappId, onSelectWhatsapp }) => {
  const classes = useStyles();
  const ui = useUi();
  const [mode, setMode] = useState("text");
  const [values, setValues] = useState(initial);
  const [file, setFile] = useState(null);
  const [showAdv, setShowAdv] = useState(false);
  const [lang, setLang] = useState("curl");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const selected = whatsApps.find((w) => String(w.id) === String(selectedWhatsappId)) || null;
  const token = selected ? selected.token : "";

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setValues((s) => ({ ...s, [k]: v }));
  };

  const req = useMemo(
    () => buildRequest(mode, { ...values, fileName: file ? file.name : undefined }),
    [mode, values, file]
  );
  const snippets = useMemo(() => generateSnippets(req, token), [req, token]);

  const handleTest = async () => {
    // TODO i18n (mensagens de validação)
    if (!token) return toast.error("Selecione uma conexão com token para testar.");
    const number = String(values.number || "").replace(/\D/g, "");
    if (!number) return toast.error("Informe o número de destino (só dígitos).");
    if (mode === "media" && !file) return toast.error("Selecione um arquivo para enviar.");
    if (mode === "image" && !String(values.url || "").trim()) return toast.error("Informe a URL da imagem.");

    const headers = { Authorization: `Bearer ${token}` };
    let data;
    if (req.kind === "multipart") {
      data = new FormData();
      data.append("number", number);
      data.append("body", values.body ? values.body : file.name);
      if (values.userId) data.append("userId", values.userId);
      if (values.queueId) data.append("queueId", values.queueId);
      if (values.sendSignature) data.append("sendSignature", "true");
      if (values.closeTicket) data.append("closeTicket", "true");
      data.append("medias", file);
      headers["Content-Type"] = "multipart/form-data";
    } else {
      headers["Content-Type"] = "application/json";
      data = req.fields.reduce((acc, f) => ({ ...acc, [f.key]: f.value }), {});
      data.number = number;
    }

    setTesting(true);
    const started = Date.now();
    try {
      const res = await axios.request({ url: req.url, method: "POST", data, headers });
      setResult({ ok: true, status: res.status, data: res.data, ms: Date.now() - started });
      // TODO i18n
      toast.success(mode === "check" ? "Consulta realizada com sucesso" : "Mensagem enviada com sucesso");
    } catch (err) {
      const status = err.response ? err.response.status : 0;
      const body = err.response && err.response.data ? err.response.data : { error: err.message };
      setResult({ ok: false, status, data: body, ms: Date.now() - started });
      toast.error(
        (body && (body.error || body.message)) || `Falha na requisição${status ? ` (HTTP ${status})` : ""}`
      );
    } finally {
      setTesting(false);
    }
  };

  const resultText = result
    ? typeof result.data === "string"
      ? result.data
      : JSON.stringify(result.data, null, 2)
    : "";

  return (
    <Card id="api-builder">
      <SectionHead
        step={2}
        // TODO i18n
        title="Envie sua primeira mensagem"
        description="Preencha à esquerda; o código à direita já sai pronto com seu token e a URL real. Teste sem sair daqui."
      />

      <div className={classes.grid}>
        {/* ---------------- formulário ---------------- */}
        <div className={classes.form}>
          <div>
            {/* TODO i18n */}
            <FieldLabel htmlFor="api-conn">Conexão</FieldLabel>
            <TextField
              id="api-conn"
              select
              fullWidth
              size="small"
              variant="outlined"
              value={selected ? String(selected.id) : ""}
              onChange={(e) => onSelectWhatsapp(e.target.value)}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled>
                {/* TODO i18n */}
                Selecione uma conexão
              </MenuItem>
              {whatsApps.map((w) => (
                <MenuItem key={w.id} value={String(w.id)} disabled={!w.token}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{w.name}</span>
                    <StatusBadge status={w.status || "DISCONNECTED"} size="sm" />
                    {!w.token && (
                      <StatusBadge color="neutral" size="sm" dot={false} label="sem token" />
                    )}
                  </span>
                </MenuItem>
              ))}
            </TextField>
          </div>

          <div>
            {/* TODO i18n */}
            <FieldLabel>Tipo de envio</FieldLabel>
            <Tabs
              className={classes.modeTabs}
              value={mode}
              onChange={(_, k) => {
                setMode(k);
                setResult(null);
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Tipo de envio"
            >
              {Object.keys(MODES).map((k) => (
                <Tab key={k} value={k} label={MODE_LABELS[k]} />
              ))}
            </Tabs>
          </div>

          <div>
            <FieldLabel htmlFor="api-number">{i18n.t("messagesAPI.textMessage.number")}</FieldLabel>
            <TextField
              id="api-number"
              fullWidth
              size="small"
              variant="outlined"
              placeholder="5511999999999"
              inputProps={{ inputMode: "numeric", style: { fontVariantNumeric: "tabular-nums" } }}
              value={values.number}
              onChange={set("number")}
            />
            <div className={ui.hint}>
              {i18n.t("messagesAPI.API.instructions.codeCountry")} + {i18n.t("messagesAPI.API.instructions.code")}{" "}
              + {i18n.t("messagesAPI.API.instructions.number")}, só dígitos.
            </div>
          </div>

          {mode === "text" && (
            <div>
              <FieldLabel htmlFor="api-body">{i18n.t("messagesAPI.textMessage.body")}</FieldLabel>
              <TextField
                id="api-body"
                fullWidth
                multiline
                minRows={3}
                size="small"
                variant="outlined"
                placeholder="Olá! Esta é uma mensagem enviada pela API."
                value={values.body}
                onChange={set("body")}
              />
            </div>
          )}

          {mode === "media" && (
            <>
              <div>
                <FieldLabel>{i18n.t("messagesAPI.mediaMessage.media")}</FieldLabel>
                <div className={classes.fileRow}>
                  <input
                    ref={fileRef}
                    type="file"
                    id="api-medias"
                    style={{ display: "none" }}
                    onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AttachFileIcon />}
                    style={{ flex: "none" }}
                    onClick={() => fileRef.current && fileRef.current.click()}
                  >
                    {/* TODO i18n */}
                    {file ? "Trocar arquivo" : "Escolher arquivo"}
                  </Button>
                  <span className={classes.fileName}>{file ? file.name : "PDF, imagem, vídeo ou áudio"}</span>
                  {file && (
                    <Tooltip title="Remover arquivo">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setFile(null);
                          if (fileRef.current) fileRef.current.value = "";
                        }}
                        aria-label="Remover arquivo"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div>
                <FieldLabel htmlFor="api-caption">{i18n.t("messagesAPI.mediaMessage.body")}</FieldLabel>
                <TextField
                  id="api-caption"
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Legenda (opcional; padrão = nome do arquivo)"
                  value={values.body}
                  onChange={set("body")}
                />
              </div>
            </>
          )}

          {mode === "image" && (
            <>
              <div>
                {/* TODO i18n */}
                <FieldLabel htmlFor="api-url">URL da imagem</FieldLabel>
                <TextField
                  id="api-url"
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="https://exemplo.com/imagem.jpg"
                  value={values.url}
                  onChange={set("url")}
                />
              </div>
              <div>
                {/* TODO i18n */}
                <FieldLabel htmlFor="api-imgcaption">Legenda</FieldLabel>
                <TextField
                  id="api-imgcaption"
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Opcional"
                  value={values.caption}
                  onChange={set("caption")}
                />
              </div>
            </>
          )}

          {(mode === "text" || mode === "media") && (
            <>
              <Button
                className={[classes.advToggle, showAdv ? classes.advOpen : null].filter(Boolean).join(" ")}
                size="small"
                endIcon={<ExpandMoreIcon />}
                onClick={() => setShowAdv((s) => !s)}
              >
                {/* TODO i18n */}
                Opções avançadas
              </Button>
              <Collapse in={showAdv}>
                <div className={classes.form}>
                  <div className={classes.row2}>
                    <div>
                      <FieldLabel htmlFor="api-user">{i18n.t("messagesAPI.textMessage.userId")}</FieldLabel>
                      <TextField
                        id="api-user"
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={values.userId}
                        onChange={set("userId")}
                        SelectProps={{ displayEmpty: true }}
                      >
                        {/* TODO i18n */}
                        <MenuItem value="">Nenhum</MenuItem>
                        {users.map((u) => (
                          <MenuItem key={u.id} value={String(u.id)}>
                            {u.name} · #{u.id}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                    <div>
                      <FieldLabel htmlFor="api-queue">{i18n.t("messagesAPI.textMessage.queueId")}</FieldLabel>
                      <TextField
                        id="api-queue"
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={values.queueId}
                        onChange={set("queueId")}
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="">Nenhuma</MenuItem>
                        {queues.map((q) => (
                          <MenuItem key={q.id} value={String(q.id)}>
                            {q.name} · #{q.id}
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                  </div>
                  <div className={classes.switches}>
                    <FormControlLabel
                      control={<Switch color="primary" size="small" checked={values.sendSignature} onChange={set("sendSignature")} />}
                      label={<span className={classes.switchLabel}>{i18n.t("messagesAPIInstructions.singMessage")}</span>}
                    />
                    <FormControlLabel
                      control={<Switch color="primary" size="small" checked={values.closeTicket} onChange={set("closeTicket")} />}
                      label={<span className={classes.switchLabel}>{i18n.t("messagesAPIInstructions.closeTicket")}</span>}
                    />
                  </div>
                </div>
              </Collapse>
            </>
          )}
        </div>

        {/* ---------------- código + teste ---------------- */}
        <div className={classes.right}>
          <div className={classes.endpoint}>
            <MethodBadge method="POST" />
            <span className={ui.path}>{req.url}</span>
          </div>

          <CodeBlock codes={snippets} tabs={LANGS} value={lang} onChange={setLang} />

          <div className={classes.testRow}>
            <Button
              variant="contained"
              color="primary"
              startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              disabled={testing}
              onClick={handleTest}
            >
              {/* TODO i18n */}
              {testing ? "Enviando…" : "Testar agora"}
            </Button>
            <span className={classes.testHint}>
              {token
                ? `Dispara a chamada real usando a conexão "${selected.name}".`
                : "Selecione uma conexão com token para testar."}
            </span>
          </div>

          {result && (
            <div>
              <div className={classes.resultHead} style={{ marginBottom: 8 }}>
                <StatusBadge
                  color={result.ok ? "success" : "error"}
                  size="sm"
                  label={result.status ? `HTTP ${result.status}` : "Sem resposta"}
                />
                <span>{result.ms} ms</span>
              </div>
              {/* TODO i18n */}
              <CodeBlock title="Resposta" code={resultText} wrap maxHeight={260} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default Builder;
